import React, { useState } from 'react';
import { Card, CardContent, Typography, Tooltip, Box, Avatar, Checkbox, Divider, Stack, Chip, Button } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PurchaseOrderItem, PurchaseOrderItemStatus } from '@domain/purchasing';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import PurchaseOrderDetails from '../../details/PurchaseOrderDetails';
import { format } from 'date-fns';
import { CardStatus } from './CardStatus';
import { Label } from '@shared/ui/common';
import { PurchaseOrderDraft } from '../../details/PurchaseOrderDraft';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';

interface DraggableOrderCardProps {
    orderItem: PurchaseOrderItem;
    onStatusChange?: (item: PurchaseOrderItem, newStatus: PurchaseOrderItemStatus) => void;
}

const DraggableOrderCard: React.FC<DraggableOrderCardProps> = ({
    orderItem,
    onStatusChange
}) => {
    const { openDrawer } = useGenericDrawer();
    const { openBottomSheet, closeBottomSheet } = useBottomSheet();
    const [optimisticStatus, setOptimisticStatus] = useState<PurchaseOrderItemStatus | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: `${orderItem.id}` });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: orderItem.purchaseOrder?.draft ? 0.5 : 1,
    };

    // Use optimistic status if available, otherwise use the actual status
    const currentStatus = optimisticStatus || orderItem.status;
    const statusHasFinished = [PurchaseOrderItemStatus.PRODUCTION_READY, PurchaseOrderItemStatus.ARRIVED, PurchaseOrderItemStatus.AVAILABLE].includes(currentStatus);

    const handleCardClick = () => {
        if (!orderItem.purchaseOrder?.id) {
            return;
        }

        if (orderItem.purchaseOrder?.draft) {
            openBottomSheet({
                title: 'Purchase Order Draft',
                component: <PurchaseOrderDraft purchaseOrderId={orderItem.purchaseOrder.id} />,
            });
            return;
        } else {
            openDrawer(
                `Purchase Order ${orderItem.purchaseOrder.poNumber || 'Details'}`,
                <PurchaseOrderDetails purchaseOrderId={orderItem.purchaseOrder.id} />
            );
        }
    }

    const handleCheckboxToggle = async (_event: React.ChangeEvent<HTMLInputElement>, status: PurchaseOrderItemStatus) => {
        if (!onStatusChange || !orderItem || isUpdating) return;

        const newStatus = getCorrespondingStatus(status);
        if (!newStatus) return;

        // Optimistically update the UI immediately
        setOptimisticStatus(newStatus);
        setIsUpdating(true);

        try {
            // Make the API call
            const result = onStatusChange(orderItem, newStatus);
            // If it returns a Promise, await it
            if (result as any instanceof Promise) {
                await result;
            }
            // If successful, clear the optimistic status
            setOptimisticStatus(null);
        } catch (error) {
            // If failed, revert to the original status
            setOptimisticStatus(null);
            console.error('Failed to update status:', error);
        } finally {
            setIsUpdating(false);
        }
    };


    const getCorrespondingStatus = (status: PurchaseOrderItemStatus) => {
        switch (status) {
            case PurchaseOrderItemStatus.IN_PRODUCTION:
                return PurchaseOrderItemStatus.PRODUCTION_READY;
            case PurchaseOrderItemStatus.IN_TRANSIT:
                return PurchaseOrderItemStatus.ARRIVED;
            case PurchaseOrderItemStatus.PRODUCTION_READY:
                return PurchaseOrderItemStatus.IN_PRODUCTION;
            case PurchaseOrderItemStatus.ARRIVED:
                return PurchaseOrderItemStatus.IN_TRANSIT;
        }
    }

    return (
        <Card
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                cursor: 'grab',
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                userSelect: 'none',
                '&:hover': {
                    boxShadow: 3
                },
            }}
            onClick={handleCardClick}
        >
            <CardContent sx={{ p: 1.5 }}>
                <Stack key={orderItem.product.id} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 2 }}>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2 }}>
                        <Avatar src={orderItem.product.image}
                            variant="rounded"
                            sx={{ width: 32, height: 32, border: 1, borderColor: 'divider' }} />

                        <Typography variant="caption"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                flex: 1,
                            }}
                        >{orderItem.product.title}</Typography>


                    </Box>

                    <Divider />

                    <Stack direction="column" alignItems="flex-start" gap={1}>
                        <Stack direction="row" gap={1}>
                            <Typography variant="subtitle2" fontWeight="medium" noWrap flex={1}>{orderItem.quantity || 0} items</Typography>


                        </Stack>

                        {orderItem.purchaseOrder?.draft ? (
                            <Label options={[{ value: 'Draft', label: 'Draft', color: 'warning' }]} currentValue="Draft" />
                        ) : (

                            <Stack direction="row" alignItems="center" gap={1}>

                                {!orderItem.shipment &&
                                    (orderItem.status === PurchaseOrderItemStatus.PRODUCTION_READY ||
                                        orderItem.status === PurchaseOrderItemStatus.IN_DELIVERY ||
                                        orderItem.status === PurchaseOrderItemStatus.ARRIVED ||
                                        orderItem.status === PurchaseOrderItemStatus.IN_TRANSIT) && (
                                        <CardStatus
                                            date={orderItem.eta || new Date()}
                                            title="ETA"
                                            tooltip={`ETA should be on ${format(orderItem.eta || new Date(), 'EEE MMM dd')}`}
                                        />
                                    )}


                                {orderItem.status == PurchaseOrderItemStatus.PLANNED && orderItem.productionStartDate && (
                                    <CardStatus
                                        date={orderItem.productionStartDate}
                                        title="Production starts"
                                        tooltip={`Production should have started on ${format(orderItem.productionStartDate, 'EEE MMM dd')}`}
                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.IN_PRODUCTION && orderItem.productionReadyDate && (
                                    <CardStatus
                                        date={orderItem.productionReadyDate}
                                        title="Production ready"
                                        tooltip={`Production should be ready on ${format(orderItem.productionReadyDate, 'EEE MMM dd')}`}

                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.PRODUCTION_READY && orderItem.shipment?.departureDate && (
                                    <CardStatus
                                        date={orderItem.shipment?.departureDate}
                                        title={`${orderItem.shipment?.transportMethod?.charAt(0).toUpperCase()}${orderItem.shipment?.transportMethod?.slice(1)} departure`}
                                        tooltip={`Departure date should be on ${format(orderItem.shipment?.departureDate, 'EEE MMM dd')}`}
                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.IN_TRANSIT && orderItem.shipment?.arrivalDate && (
                                    <CardStatus
                                        date={orderItem.shipment?.arrivalDate}
                                        title="Arrival"
                                        tooltip={`Boat should have arrived on ${format(orderItem.shipment?.arrivalDate, 'EEE MMM dd')}`}
                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.ARRIVED && orderItem.shipment?.processedDate && (
                                    <CardStatus
                                        date={orderItem.shipment?.processedDate}
                                        title="Processed"
                                        tooltip={`Processed date should be on ${format(orderItem.shipment?.processedDate, 'EEE MMM dd')}`}
                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.IN_DELIVERY && orderItem.shipment?.processedDate && (
                                    <CardStatus
                                        date={orderItem.shipment?.processedDate}
                                        title="Delivered"
                                        tooltip="Processed"
                                    />
                                )}

                                {orderItem.status == PurchaseOrderItemStatus.AVAILABLE && (
                                    <Chip label={"Available" + (orderItem.shipment ? (" in " + orderItem.shipment?.destinationWarehouse?.name) : '')} color="success" size="small" />
                                )}
                            </Stack>
                        )}

                    </Stack>

                    {getCorrespondingStatus(currentStatus) && (

                        <Stack direction="row" alignItems="center" flexGrow={1} justifyContent="flex-start" gap={1}>
                            <Tooltip title={statusHasFinished ? "Mark as not ready" : "Mark as ready"}>
                                <Checkbox
                                    sx={{
                                        padding: 0
                                    }}
                                    checked={statusHasFinished}
                                    onChange={(event) => handleCheckboxToggle(event, currentStatus)}
                                    onClick={(event) => event.stopPropagation()}
                                    disabled={isUpdating}
                                    size="small"
                                />
                            </Tooltip>

                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {statusHasFinished ? currentStatus : getCorrespondingStatus(currentStatus)}
                            </Typography>


                        </Stack>


                    )}



                </Stack>
            </CardContent>


        </Card>
    );
};

export default DraggableOrderCard; 